import React from 'react';

/**
 * One value, controlled by the caller or kept here, always handed to React
 * Aria as controlled: a picker can then hold it still (read-only), snap it
 * back (a suppressed option) or write it in step with another value, without
 * React Aria ever switching between its controlled and uncontrolled modes.
 * The setter reports only a change, as React Aria's own controlled state
 * does, so a form library is not re-validated for nothing, and keeps one
 * identity whatever the caller's callback does, so an effect keyed on it
 * runs only when the value moves. Shared by direct import, never through
 * the barrel.
 */
export function useOwnedState<V>(
  value: V | undefined,
  defaultValue: V,
  onChange?: (next: V) => void,
  isEqual: (a: V, b: V) => boolean = Object.is,
) {
  const [internal, setInternal] = React.useState(defaultValue);
  /* Once a caller has passed a value it owns it: a later undefined (a form
   * reset with no default) means none, not the last value kept here. */
  const wasControlled = React.useRef(false);
  if (value !== undefined) wasControlled.current = true;
  const isControlled = wasControlled.current;
  const current = isControlled ? (value === undefined ? defaultValue : value) : internal;
  const latest = React.useRef({ current, onChange, isEqual });
  latest.current = { current, onChange, isEqual };
  /* A change reported but not rendered yet: React Aria can repeat one in the
   * same tick (a commit on blur), which must not report twice. It lasts only
   * that tick, so a caller that refuses the change can be asked again. */
  const pending = React.useRef<{ value: V } | null>(null);
  const set = React.useCallback(
    (next: V) => {
      const base = pending.current ? pending.current.value : latest.current.current;
      if (latest.current.isEqual(next, base)) return;
      pending.current = { value: next };
      queueMicrotask(() => {
        pending.current = null;
      });
      if (!isControlled) setInternal(next);
      latest.current.onChange?.(next);
    },
    [isControlled],
  );
  return [current, set] as const;
}

/** A picker's value: one id or none, or the ids of the multiple form. */
export type PickerValue = string | null | string[];

/** Two picker values are the same when they hold the same ids in the same order. */
export function samePickerValue(a: PickerValue, b: PickerValue) {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((id, index) => id === b[index]);
  }
  return Object.is(a, b);
}
